import React, { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  GET_ALL_SAVED_FORM_DETAILS_BY_FORM_UUID,
  GET_FORM_BUILDER_DETAILS_BY_FORM_UUID
} from '../../../graphql/query'
import { useQuery } from '@apollo/client'
import PrefillEditsReviewModal from './PrefillEditsReviewModal'
import {
  enrichPrefillFieldEditsFromSubmission,
  formatEditReviewStatus,
  getEditHighlightClass,
  type PrefillFieldEdit
} from './submissionPrefillEditUtils'
import {
  buildFlatSubmissionColumns,
  buildSubmissionExportRows,
  extractTableFieldsForDisplay,
  getFieldEditForColumn,
  normalizeFormData,
  normalizeFormValueEntries,
  resolveSubmissionCellEditStatus,
  resolveSubmissionDisplayValue
} from './submissionTableFlatten'
import { prefillApprovalRequired } from '../../../pages/processAutomation/formBuilder/formBuilderModel/formTypeUtils'
import { SubmissionEditValueDisplay, buildFormFieldMetaById } from './submissionEditValueDisplay'
import './submissionEditValueDisplay.scss'
import * as XLSX from 'xlsx'

const TableSkeleton = () => {
  const SkeletonRow = () => (
    <tr>
      {Array.from({ length: 5 }).map((_, i) => (
        <td key={i}>
          <div className='skeleton-cell'></div>
        </td>
      ))}
    </tr>
  )

  return (
    <div className='card-body pt-0'>
      {/* Inline styles for the skeleton animation */}
      <style>{`
        .skeleton-cell {
          width: 100%;
          height: 20px;
          background-color: #f0f3f5;
          border-radius: 4px;
          animation: skeleton-loading 1.5s infinite ease-in-out;
        }
        @keyframes skeleton-loading {
          0% { background-color: #f0f3f5; }
          50% { background-color: #e9ecef; }
          100% { background-color: #f0f3f5; }
        }
      `}</style>
      <div className='table-responsive'>
        <table className='table align-middle table-row-dashed fs-6 gy-5'>
          <thead>
            <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
              {Array.from({ length: 5 }).map((_, i) => (
                <th key={i} className='min-w-125px'>
                  <div className='skeleton-cell' style={{ height: '24px' }}></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='fw-semibold text-gray-600'>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Error component to display when the query fails
const ErrorDisplay = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
  <div className='d-flex flex-column align-items-center justify-content-center p-10 text-center'>
    <i className='bi bi-x-circle-fill text-danger fs-3x mb-4'></i>
    <h3 className='text-gray-800 fw-bold mb-2'>An Error Occurred</h3>
    <p className='text-muted mb-4'>{message}</p>
    <button onClick={onRetry} className='btn btn-primary'>
      Try Again
    </button>
  </div>
)

// Empty state component when no data is available
const EmptyState = () => (
  <div className='d-flex flex-column align-items-center justify-content-center p-10 text-center'>
    <i className='bi bi-file-earmark-x fs-3x text-muted mb-4'></i>
    <h3 className='text-gray-700 fw-bold mb-2'>No Submissions Yet</h3>
    <p className='text-muted'>There is no data to display for this form.</p>
  </div>
)

// --- Main Component ---

export const SubmissionDetails = () => {
  const navigate = useNavigate()
  const { uuid } = useParams()
  const [currentPage, setCurrentPage] = useState(1)
  const [reviewSubmission, setReviewSubmission] = useState<any | null>(null)
  const ITEMS_PER_PAGE = 10

  const { loading, error, data, refetch } = useQuery(GET_ALL_SAVED_FORM_DETAILS_BY_FORM_UUID, {
    variables: {
      input: {
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
        formUuid: uuid
      }
    },
    // The context header is non-standard and kept as is from the original code.
    context: {
      headers: {
        'access-control-allow-methods': 'Form-Builder'
      }
    },
    notifyOnNetworkStatusChange: true // Better loading states on refetch
  })

  const { data: formDetailsData } = useQuery(GET_FORM_BUILDER_DETAILS_BY_FORM_UUID, {
    variables: { input: { formUuid: uuid } },
    skip: !uuid,
    context: {
      headers: {
        'access-control-allow-methods': 'Form-Builder'
      }
    }
  })

  const formData = normalizeFormData(
    formDetailsData?.getFormBuilderDetailsByFormUuid?.form?.formData
  )

  // --- Memoized data for performance and readability ---
  const submissionData = useMemo(() => data?.getAllSavedFormDetailsByFormUuid?.data, [data])
  const submissions = useMemo(() => submissionData?.savedFormDetails || [], [submissionData])
  const totalSubmissions = useMemo(() => submissionData?.totalSavedForms || 0, [submissionData])
  const totalPages = Math.ceil(totalSubmissions / ITEMS_PER_PAGE)
  const hasSubmissions = submissions.length > 0

  const showPrefillEditReview = prefillApprovalRequired(submissionData)

  const submissionFormValues = useMemo(
    () => submissions.flatMap((submission: any) => normalizeFormValueEntries(submission.formValue)),
    [submissions]
  )

  const tableMetaByFieldId = useMemo(
    () => extractTableFieldsForDisplay(formData, submissionFormValues),
    [formData, submissionFormValues]
  )

  const fieldMetaById = useMemo(() => buildFormFieldMetaById(formData), [formData])

  const flatColumns = useMemo(
    () => buildFlatSubmissionColumns(formData, submissionFormValues),
    [formData, submissionFormValues]
  )

  const getPendingEditCount = (submission: any) => {
    const edits: PrefillFieldEdit[] = submission.prefillFieldEdits || []
    return edits.filter((e) => e.status === 'pending').length
  }

  // --- Event Handlers ---
  const handleNavigateBack = () => navigate('/automation/process-automation/form-builder')
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }
  const nextPage = () => handlePageChange(currentPage + 1)
  const prevPage = () => handlePageChange(currentPage - 1)

  const handleExportExcel = async () => {
    if (!totalSubmissions) return

    try {
      const { data: exportData } = await refetch({
        input: { offset: 0, limit: totalSubmissions, formUuid: uuid }
      })

      const allSubmissions = exportData?.getAllSavedFormDetailsByFormUuid?.data?.savedFormDetails
      if (!allSubmissions || allSubmissions.length === 0) {
        alert('No data available to export.')
        return
      }

      const { headers, rows } = buildSubmissionExportRows(formData, allSubmissions, {
        includeRecipientColumns: false
      })

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows])
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Submissions')
      const fileName = `${submissionData?.formName || 'report'}_${
        new Date().toISOString().split('T')[0]
      }.xlsx`
      XLSX.writeFile(workbook, fileName)
    } catch (err) {
      console.error('Error exporting Excel:', err)
      alert('An error occurred while exporting the data.')
    }
  }

  // --- Render Logic ---
  if (error) {
    return (
      <div className='card shadow-sm'>
        <ErrorDisplay message={error.message} onRetry={() => refetch()} />
      </div>
    )
  }

  return (
    <>
      <div className='card shadow-sm mb-4'>
        {/* --- Card Header: Title & Back Button --- */}
        <div className='card-header border-0 pt-6'>
          <div className='d-flex align-items-center'>
            <button
              onClick={handleNavigateBack}
              className='btn btn-icon btn-sm btn-light-primary me-5'
              title='Back to Form Builder'
            >
              <i className='bi bi-arrow-left fs-2'></i>
            </button>
            <div>
              <h3 className='card-title align-items-start flex-column mb-0'>
                <span className='fw-bold text-gray-800 fs-2'>
                  {submissionData?.formName.replace(/[_\-]/g, ' ').replace(/[^\w\s]/gi, '') ||
                    'Submissions'}
                </span>
                <span className='text-muted mt-1 fw-semibold fs-7'>
                  {submissionData?.fromDescription || `A total of ${totalSubmissions} entries`}
                </span>
              </h3>
            </div>
          </div>
        </div>

        {/* --- Card Toolbar: Search & Export --- */}
        <div className='card-header border-0 pt-2 pb-5 justify-content-end'>
          {/* <div className='d-flex align-items-center position-relative my-1'>
          <i className='ki-duotone ki-magnifier fs-3 position-absolute ms-4'>
            <span className='path1'></span>
            <span className='path2'></span>
          </i>
          <input
            type='text'
            className='form-control form-control-solid w-250px ps-12'
            placeholder='Search Submissions'
            // NOTE: Search functionality is not implemented in the original logic.
            // Add onChange handler here to implement it.
          />
        </div> */}
          <div className='card-toolbar'>
            <button
              type='button'
              className='btn btn-light-primary'
              onClick={handleExportExcel}
              disabled={!hasSubmissions || loading}
            >
              <i className='ki-duotone ki-exit-up fs-2'>
                <span className='path1'></span>
                <span className='path2'></span>
              </i>
              Export as Excel
            </button>
          </div>
        </div>

        {/* --- Card Body: Table or State Components --- */}
        <style>{`
        .submission-cell-edited {
          border-radius: 6px;
          padding: 0.35rem 0.5rem;
        }
        .submission-cell-edited--pending {
          background-color: #fff8dd;
          border: 1px solid #ffc700;
        }
        .submission-cell-edited--approved {
          background-color: #e8fff3;
          border: 1px solid #50cd89;
        }
        .submission-cell-edited--rejected {
          background-color: #fff5f8;
          border: 1px solid #f1416c;
        }
        .submission-details-table th.submission-conditional-col,
        .submission-details-table td.submission-conditional-col {
          background-color: #f5f3ff;
        }
        .submission-details-table th.submission-conditional-col {
          box-shadow: inset 0 -2px 0 #c4b5fd;
        }
      `}</style>
        {loading && !data ? (
          <TableSkeleton />
        ) : hasSubmissions ? (
          <>
            <div className='card-body pt-0'>
              <div className='table-responsive'>
                <table className='table align-middle table-row-dashed fs-6 gy-5 submission-details-table'>
                  <thead>
                    <tr className='text-start fw-bold fs-7 gs-0'>
                      {flatColumns.map((col) => (
                        <th
                          key={col.key}
                          className={`min-w-200px${
                            col.isConditionalEdit ? ' submission-conditional-col' : ''
                          }`}
                        >
                          {col.isTableCell && (
                            <span
                              className='badge bg-light text-info border fw-semibold fs-9 me-2'
                              title={`Table: ${col.tableLabel}`}
                            >
                              table
                            </span>
                          )}
                          {col.isConditionalEdit && (
                            <span
                              className='badge bg-light text-primary border fw-semibold fs-9 me-2'
                              title='Conditional edit field'
                            >
                              edit
                            </span>
                          )}
                          {col.label}
                        </th>
                      ))}
                      {showPrefillEditReview && (
                        <th className='min-w-200px text-uppercase'>Edit review</th>
                      )}
                      <th className='min-w-200px text-uppercase'>Submitted At</th>
                    </tr>
                  </thead>
                  <tbody className='fw-semibold text-gray-600'>
                    {submissions.map((submission: any, index: number) => (
                      <tr key={submission.savedFormUuid || index} className='hoverable'>
                        {flatColumns.map((col) => {
                          const fieldEdit = getFieldEditForColumn(submission.prefillFieldEdits, col)
                          const { displayValue, pendingSubmittedValue } =
                            resolveSubmissionDisplayValue(
                              col,
                              normalizeFormValueEntries(submission.formValue),
                              fieldEdit,
                              tableMetaByFieldId
                            )
                          // Keep empty string (do not coerce to "—") — the display component
                          // renders "—" for empty values. Coercing broke file fields: "—" was
                          // treated as a file URL and showed a bogus "View file" button.
                          const cellValue = displayValue || ''
                          const highlightClass = getEditHighlightClass(
                            resolveSubmissionCellEditStatus(col, fieldEdit, tableMetaByFieldId)
                          )
                          const cellEditStatus = resolveSubmissionCellEditStatus(
                            col,
                            fieldEdit,
                            tableMetaByFieldId
                          )

                          return (
                            <td
                              key={col.key}
                              className={
                                col.isConditionalEdit ? 'submission-conditional-col' : undefined
                              }
                            >
                              <div className={highlightClass}>
                                <div
                                  className={
                                    col.isTableCell ? 'submission-table-cell-display' : undefined
                                  }
                                >
                                  <SubmissionEditValueDisplay
                                    value={cellValue}
                                    fieldId={col.fieldId}
                                    fieldMetaById={fieldMetaById}
                                    tableFieldId={col.isTableCell ? col.fieldId : undefined}
                                    tableColIndex={col.isTableCell ? col.colIndex : undefined}
                                    editFieldType={col.editFieldType}
                                    muted={cellEditStatus === 'rejected'}
                                  />
                                </div>
                                {cellEditStatus === 'pending' &&
                                pendingSubmittedValue &&
                                pendingSubmittedValue !== cellValue ? (
                                  <small className='text-muted d-block mt-1'>
                                    Proposed:{' '}
                                    <SubmissionEditValueDisplay
                                      value={pendingSubmittedValue}
                                      fieldId={col.fieldId}
                                      fieldMetaById={fieldMetaById}
                                      tableFieldId={col.isTableCell ? col.fieldId : undefined}
                                      tableColIndex={col.isTableCell ? col.colIndex : undefined}
                                      editFieldType={col.editFieldType}
                                    />
                                  </small>
                                ) : null}
                                {cellEditStatus === 'pending' && !pendingSubmittedValue ? (
                                  <small className='text-muted d-block mt-1'>
                                    Pending approval
                                  </small>
                                ) : null}
                                {cellEditStatus === 'approved' ? (
                                  <small className='text-muted d-block mt-1'>Approved change</small>
                                ) : null}
                                {cellEditStatus === 'rejected' ? (
                                  <small className='text-muted d-block mt-1'>Change rejected</small>
                                ) : null}
                              </div>
                            </td>
                          )
                        })}
                        {showPrefillEditReview && (
                          <td>
                            {Array.isArray(submission.prefillFieldEdits) &&
                            submission.prefillFieldEdits.length > 0 ? (
                              <div className='d-flex flex-column gap-2'>
                                <span className='small text-muted'>
                                  {formatEditReviewStatus(submission.prefillFieldEdits)}
                                </span>
                                <button
                                  type='button'
                                  className={`btn btn-sm ${
                                    getPendingEditCount(submission) > 0
                                      ? 'btn-warning'
                                      : 'btn-light-primary'
                                  }`}
                                  onClick={() => setReviewSubmission(submission)}
                                >
                                  {getPendingEditCount(submission) > 0
                                    ? `Review (${getPendingEditCount(submission)} pending)`
                                    : 'View edits'}
                                </button>
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                        )}
                        <td>
                          {new Date(submission.updatedAt).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* --- Pagination --- */}
            <div className='card-footer d-flex flex-stack'>
              <div className='text-muted'>
                Showing page <span className='fw-bold'>{currentPage}</span> of{' '}
                <span className='fw-bold'>{totalPages}</span>
              </div>
              <ul className='pagination'>
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button onClick={prevPage} className='page-link'>
                    Previous
                  </button>
                </li>
                {/* Consider a more advanced pagination component for many pages */}
                <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                  <button onClick={nextPage} className='page-link'>
                    Next
                  </button>
                </li>
              </ul>
            </div>
          </>
        ) : (
          <EmptyState />
        )}
      </div>

      {reviewSubmission ? (
        <PrefillEditsReviewModal
          isOpen={Boolean(reviewSubmission)}
          onClose={() => setReviewSubmission(null)}
          saveFormUuid={reviewSubmission.saveFormUuid}
          recipientName={reviewSubmission.recipientName}
          recipientEmail={reviewSubmission.recipientEmail}
          edits={enrichPrefillFieldEditsFromSubmission(
            reviewSubmission.prefillFieldEdits || [],
            formData,
            reviewSubmission.formValue
          )}
          formData={formData}
          onReviewed={() => refetch()}
        />
      ) : null}
    </>
  )
}
